import { Command } from 'commander'
import { createApiClient, type ApiClient } from '../http.js'
import { execution } from '../api/catalog.js'
import { call, callRaw, pageItems, pagingQuery, type PagedResult, type PagingFlags } from '../api/client.js'
import { resolveWorkspaceId } from '../context.js'
import { output, outputRecord, printJson } from '../output.js'

export interface ExecutionDto {
  id: string
  workspaceId: string
  engineId: string
  engineName?: string
  filePath?: string
  mode?: string
  trigger?: string
  tool?: string
  status: string
  requestedAt?: string
  startedAt?: string
  completedAt?: string
  result?: { status?: string; data?: unknown; artifacts?: string[]; error?: string } | null
  progressPercent?: number | null
  progressMessage?: string | null
  [key: string]: unknown
}

interface ExecutionLogsDto {
  stdout?: string
  stderr?: string
  running?: boolean
  truncated?: boolean
}

const TERMINAL_STATUSES = new Set(['Completed', 'Failed', 'Cancelled', 'TimedOut'])

export function registerExec(program: Command): void {
  const execCmd = program.command('exec').description('Run and inspect executions')

  execCmd
    .command('run <filePath>')
    .description('Execute a workspace file with its engine (e.g. `verentis exec run /scripts/transform.py`)')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--engine <engineId>', 'explicit engine id (default: resolved from the file type)')
    .option('--tool <tool>', 'engine tool to invoke (default: the engine default, e.g. execute)')
    .option('--mode <mode>', 'request-response | long-running')
    .option('--arg <value...>', 'command-line arguments passed to the script (repeatable)')
    .option('--params <json>', 'tool parameters JSON (engine.context.input.parameters)')
    .option('--follow', 'wait for completion and stream logs')
    .option('--json', 'output the execution record as JSON')
    .action(async (filePath: string, options: { workspace?: string; branch: string; engine?: string; tool?: string; mode?: string; arg?: string[]; params?: string; follow?: boolean; json?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      if (options.params) JSON.parse(options.params) // validate early

      const result = await call<ExecutionDto>(api, execution.request, {
        workspaceId,
        body: {
          workspaceId,
          engineId: options.engine ?? null,
          filePath: filePath.startsWith('/') ? filePath : `/${filePath}`,
          branch: options.branch,
          tool: options.tool ?? null,
          mode: options.mode ?? null,
          arguments: options.arg ?? [],
          parameters: options.params ?? null,
          clientRuntimeCapable: false,
        },
      })

      if (options.json && !options.follow) {
        printJson(result)
        return
      }
      console.log(`Execution ${result.id} → ${result.status} (engine ${result.engineName ?? result.engineId})`)

      if (options.follow) {
        await followExecution(api, workspaceId, result.id, options.json)
      } else if (!TERMINAL_STATUSES.has(result.status)) {
        console.log(`Follow with: verentis exec logs ${result.id} --follow`)
      } else {
        printResult(result)
      }
    })

  execCmd
    .command('list')
    .description('List executions in a workspace')
    .option('--workspace <workspaceId>')
    .option('--file <filePath>', 'filter by workspace file path')
    .option('--page <n>')
    .option('--page-size <n>')
    .option('--order-by <field>')
    .option('--json', 'output JSON')
    .action(async (options: PagingFlags & { workspace?: string; file?: string; json?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const page = await call<PagedResult<ExecutionDto>>(api, execution.list, {
        workspaceId,
        query: { workspaceId, filePath: options.file, ...pagingQuery(options) },
      })
      output(
        pageItems(page).map((exec) => ({
          id: exec.id,
          status: exec.status,
          engine: exec.engineName ?? exec.engineId,
          file: exec.filePath ?? '',
          requestedAt: exec.requestedAt ?? '',
        })),
        { id: 'Id', status: 'Status', engine: 'Engine', file: 'File', requestedAt: 'Requested' },
        options,
        page,
      )
    })

  execCmd
    .command('get <executionId>')
    .description('Show execution details (status, result, artifacts)')
    .option('--workspace <workspaceId>')
    .option('--json', 'output JSON')
    .action(async (executionId: string, options: { workspace?: string; json?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const result = await call<ExecutionDto>(api, execution.get, {
        params: { id: executionId },
        workspaceId,
        query: { workspaceId },
      })
      if (options.json) {
        printJson(result)
        return
      }
      const { result: execResult, ...rest } = result
      outputRecord(rest as Record<string, unknown>, options)
      printResult(result)
    })

  execCmd
    .command('cancel <executionId>')
    .description('Cancel a running execution')
    .option('--workspace <workspaceId>')
    .action(async (executionId: string, options: { workspace?: string }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const result = await call<ExecutionDto>(api, execution.cancel, {
        params: { id: executionId },
        workspaceId,
        body: { id: executionId, workspaceId },
      })
      console.log(`Execution ${executionId} → ${result?.status ?? 'Cancelled'}.`)
    })

  execCmd
    .command('logs <executionId>')
    .description('Fetch (or stream with --follow) the logs of an execution')
    .option('--workspace <workspaceId>')
    .option('--follow', 'stream live logs via SSE until the execution ends')
    .action(async (executionId: string, options: { workspace?: string; follow?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      if (options.follow) {
        await streamLogs(api, workspaceId, executionId)
        return
      }
      const logs = await call<ExecutionLogsDto>(api, execution.logs, {
        params: { id: executionId },
        workspaceId,
        query: { workspaceId },
      })
      if (logs.stdout) process.stdout.write(logs.stdout.endsWith('\n') ? logs.stdout : `${logs.stdout}\n`)
      if (logs.stderr) process.stderr.write(logs.stderr.endsWith('\n') ? logs.stderr : `${logs.stderr}\n`)
      if (logs.truncated) console.error('(logs truncated — the full log exceeded the inline limit)')
      if (logs.running) console.error('(execution still running — use --follow to stream)')
    })

  const engineCmd = program.command('engine').description('Inspect execution engines')

  engineCmd
    .command('list')
    .description('List registered execution engines')
    .option('--json', 'output JSON')
    .action(async (options: { json?: boolean }) => {
      const api = await createApiClient()
      const page = await call<PagedResult<Record<string, unknown>>>(api, execution.listEngines, {
        query: { PageNo: 1, PageSize: 100 },
      })
      output(pageItems(page), { name: 'Name', displayName: 'Display', version: 'Version', id: 'Id' }, options, page)
    })

  engineCmd
    .command('get <engineId>')
    .description('Show engine details (runtimes, tools, permissions)')
    .action(async (engineId: string) => {
      const api = await createApiClient()
      const result = await call<unknown>(api, execution.getEngine, { params: { id: engineId } })
      printJson(result)
    })

  engineCmd
    .command('resolve <fileTypeOrPath>')
    .description('Resolve which engine handles a MIME type or file path')
    .option('--workspace <workspaceId>')
    .action(async (fileTypeOrPath: string, options: { workspace?: string }) => {
      const api = await createApiClient()
      const workspaceId = await resolveWorkspaceId(options, false)
      const query = fileTypeOrPath.includes('/') && !fileTypeOrPath.includes('.')
        ? { fileType: fileTypeOrPath }
        : fileTypeOrPath.includes('.') && !fileTypeOrPath.includes('/')
          ? { fileType: fileTypeOrPath }
          : { filePath: fileTypeOrPath }
      const result = await call<unknown>(api, execution.resolveEngine, {
        query: { ...query, ...(workspaceId ? { workspaceId } : {}) },
      })
      printJson(result)
    })

  engineCmd
    .command('sync')
    .description('Re-scan the workspace for engine manifests and sync registrations')
    .option('--workspace <workspaceId>')
    .action(async (options: { workspace?: string }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const result = await call<unknown>(api, execution.syncEngines, { workspaceId, body: { workspaceId } })
      printJson(result)
    })
}

function printResult(exec: ExecutionDto): void {
  if (!exec.result) return
  if (exec.result.error) console.error(`Error: ${exec.result.error}`)
  if (exec.result.data !== undefined && exec.result.data !== null) {
    console.log('Result:')
    printJson(exec.result.data)
  }
  for (const artifact of exec.result.artifacts ?? []) console.log(`Artifact: ${artifact}`)
}

/** Stream `text/event-stream` frames, writing `data:` payload lines to stdout until `event: end`. */
export async function streamLogs(api: ApiClient, workspaceId: string, executionId: string): Promise<void> {
  const res = await callRaw(api, execution.logsStream, {
    params: { id: executionId },
    workspaceId,
    query: { workspaceId },
  })
  if (!res.body) return

  const decoder = new TextDecoder()
  let buffer = ''
  let currentEvent = 'message'
  const reader = (res.body as ReadableStream<Uint8Array>).getReader()
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    let newlineIdx: number
    while ((newlineIdx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, newlineIdx).replace(/\r$/, '')
      buffer = buffer.slice(newlineIdx + 1)
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ')) {
        if (currentEvent === 'end') return
        console.log(line.slice(6))
      } else if (line === '') {
        currentEvent = 'message'
      }
    }
  }
}

/** Poll an execution to terminal status while streaming its logs. */
async function followExecution(api: ApiClient, workspaceId: string, executionId: string, json?: boolean): Promise<void> {
  await streamLogs(api, workspaceId, executionId)
  // Stream ended — poll briefly for the final record (result persistence can lag the stream).
  for (let attempt = 0; attempt < 30; attempt++) {
    const exec = await call<ExecutionDto>(api, execution.get, {
      params: { id: executionId },
      workspaceId,
      query: { workspaceId },
    })
    if (TERMINAL_STATUSES.has(exec.status)) {
      if (json) printJson(exec)
      else {
        console.log(`Status: ${exec.status}`)
        printResult(exec)
      }
      if (exec.status !== 'Completed') process.exitCode = 1
      return
    }
    await new Promise((resolve) => setTimeout(resolve, 1000))
  }
  console.error('Timed out waiting for a terminal status — check `verentis exec get`.')
  process.exitCode = 1
}
